
import {
  useState,
  useMemo,
  useCallback,
  useEffect
} from 'react';
import { useHistory, useLocation } from "react-router";
import { SelectedType } from '@atlaskit/tabs/types';
import { URL } from "../../constants/navigation";
import { MainLayout } from "../../layout/MainLayout";
import { Grid } from "../../UI/Grid";
import { Tabs } from "../../UI/Tabs";
import { TabList } from "../../UI/TabList";
import { Tab } from "../../UI/Tab";
import { TabPanel } from "../../UI/TabPanel";
import { Typography } from "../../UI/Typography";
import { Link } from "../../UI/Link";
import { TablePaginationProvider } from "../../components/TablePagination/context";
import { handleNavigation } from "../../helpers/handleNavigation";

import { Community } from "../../components/Community";
import { PlacementWorksheetRulesTable } from "../../components/PlacementWorksheetRulesTable";
import { PostMatchingVisibilityToggle } from "../../components/PostMatchingVisibilityToggle";

const COMMUNITY_DESCRIPTION = "This information will be displayed to users on the Community section of the RoomSync app.";
const POST_MATCHING_VISIBILITY_DESCRIPTION = "When enabled, residents can view their matches and start new chats once the matching round is closed or has moved to active - post matching status.";

const tabs = [
  "community-info",
  "placement-worksheet",
  "post-matching-visibility"
];

export const GeneralSettingsPage = () => {
  const [ selectedTab, setSelectedTab ] = useState(0);
  const { push } = useHistory();
  const location = useLocation();

  const handlePlacementWorksheetClick = useCallback((event: React.MouseEvent) => {
    handleNavigation(event, URL.PLACEMENT_WORKSHEET, push);
  }, [push]);

  const handleUpdate = useCallback(
    (index: SelectedType) => {
      setSelectedTab(index);
      push(URL.GENERAL_SETTINGS_BASE + tabs[index]);
    },
    [setSelectedTab],
  );

  // Set initial tab based on URL
  useEffect(() => {
    const currentPath = location.pathname;
    const tabSegment = currentPath.replace(URL.GENERAL_SETTINGS_BASE, '');
    const tabIndex = tabs.indexOf(tabSegment);
    if (tabIndex !== -1) {
      setSelectedTab(tabIndex);
    }
  }, [location.pathname]);

  const PLACEMENT_WORKSHEET_RULES_DESCRIPTION = useMemo (() => <>
    The Placement Worksheet is an optional tool that administrators can use to draft unit and room placements.
    Administrators can also use this tool to sort and see available rooms based on the placements you've chosen.
    This tool is internal and placements drafted on it are not visible to residents anywhere in RoomSync.
    When enabled, the Placement Worksheet is visible at <Link onClick={handlePlacementWorksheetClick}>{' Users > Place'}</Link>.
  </>, [handlePlacementWorksheetClick]);
  
  return (
    <MainLayout title="General Settings">
      
      <Tabs 
        id="match-rules-tabs"
        shouldUnmountTabPanelOnChange 
        onChange={handleUpdate}
        selected={selectedTab}>
            <TabList>
              <Tab>Community Info</Tab>
              <Tab>Placement Worksheet</Tab>
              <Tab>Post Matching Matches Visibility</Tab>
            </TabList>
            <TabPanel>
              <Grid item xs={12} pt={2}>
                <Typography variant="body2" mb={2}>
                  {COMMUNITY_DESCRIPTION}
                </Typography>
                <Community />
              </Grid>
            </TabPanel>
            <TabPanel>
              <Grid item xs={12} pt={2}>
                <Typography variant="body2" mb={2}>
                  {PLACEMENT_WORKSHEET_RULES_DESCRIPTION}
                </Typography>
                <TablePaginationProvider>
                  <PlacementWorksheetRulesTable/>
                </TablePaginationProvider>
              </Grid>
            </TabPanel>
            <TabPanel>
              <Grid item xs={12} pt={2}>
                <Typography variant="body2" mb={2}>
                  {POST_MATCHING_VISIBILITY_DESCRIPTION}
                </Typography>
                <PostMatchingVisibilityToggle />
              </Grid>
            </TabPanel>
          </Tabs>
    </MainLayout>
  );
};
